/**
 * ч.1436 (рішення власника): перелік очікувачів обробки 1С, якої ще немає (BAS, керована форма,
 * Бухгалтерія ред. 2.0). Коли обробка готова — «сповістити», і сервер напише кожному, хто чекає:
 * у Telegram, якщо підключено, інакше на пошту.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { platformMigration1c, type WaitlistUser, type WaitlistVariant } from '@/api/platformMigration1c';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('uk-UA', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—');
const VIA: Record<string, string> = { telegram: 'Telegram', email: 'пошта' };

const COLUMNS: ColumnDef<WaitlistUser>[] = [
  { key: 'email', header: 'Email', sortValue: (u) => u.email, cell: (u) => u.email },
  { key: 'full_name', header: 'Імʼя', sortValue: (u) => u.full_name, cell: (u) => u.full_name },
  { key: 'tenant_name', header: 'Акаунт', sortValue: (u) => u.tenant_name ?? '', cell: (u) => u.tenant_name ?? '—' },
  { key: 'telegram', header: 'Telegram', width: 100, sortValue: (u) => (u.telegram ? 1 : 0),
    cell: (u) => (u.telegram ? 'підключено' : '—') },
  { key: 'created_at', header: 'Записався', width: 150, sortValue: (u) => u.created_at, cell: (u) => when(u.created_at) },
  { key: 'notified_at', header: 'Повідомлено', width: 190, sortValue: (u) => u.notified_at ?? '',
    cell: (u) => (u.notified_at ? `${when(u.notified_at)} · ${VIA[u.notified_via ?? ''] ?? ''}` : 'чекає') },
];

export default function ProcessingWaitlistTab() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const q = useQuery({ queryKey: ['platform-processing-waitlist'], queryFn: platformMigration1c.waitlist });
  const [result, setResult] = useState<string | null>(null);
  const notify = useMutation({
    mutationFn: (v: WaitlistVariant['variant']) => platformMigration1c.notifyWaitlist(v),
    onSuccess: (r) => {
      setResult(`Надіслано: у Telegram — ${r.telegram}, на пошту — ${r.email}`
        + (r.failed ? `; не вдалося — ${r.failed} (лишились у переліку)` : '') + '.');
      qc.invalidateQueries({ queryKey: ['platform-processing-waitlist'] });
    },
    onError: () => setResult('Не вдалося розіслати повідомлення.'),
  });
  const ask = async (v: WaitlistVariant) => {
    const ok = await confirm({
      title: 'Обробка готова?',
      message: `Повідомити ${v.waiting} очікувачів, що обробка для «${v.label}» готова? `
        + 'Кожен отримає повідомлення у Telegram, якщо його підключено, інакше на пошту.',
      okLabel: 'сповістити', cancelLabel: 'скасувати',
    });
    if (ok) notify.mutate(v.variant);
  };
  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Перелік очікувачів</h2>
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        Хто на сторінці перенесення натиснув «сповістити про готовність» для обробки, якої ще немає.
        Коли обробка готова і лежить на сервері, натисніть «сповістити» біля варіанта.
      </p>
      {result && <div className="mc__panel" role="status">{result}</div>}
      {q.isLoading && <p>Завантаження…</p>}
      {q.data?.map((v) => (
        <section key={v.variant} style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0' }}>
            <h3 style={{ margin: 0 }}>{v.label}</h3>
            <span style={{ color: 'var(--muted)' }}>чекають {v.waiting}, повідомлено {v.notified}</span>
            <button type="button" className="primary" style={{ marginLeft: 'auto' }}
              disabled={v.waiting === 0 || notify.isPending}
              title={v.waiting === 0 ? 'Ніхто не чекає' : undefined}
              onClick={() => void ask(v)}>
              сповістити
            </button>
          </div>
          <DataTable rows={v.users} columns={COLUMNS} rowKey={(u) => `${u.email}|${u.created_at}`}
            emptyText="Ніхто не записався" initialSortKey="created_at" />
        </section>
      ))}
    </div>
  );
}
